import React, { useRef, useState, Dispatch, SetStateAction, useEffect } from 'react';
import { Button, Row, Spinner, Col, Form } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { ResultVerification } from '../../../models/interfaces/Match';
import SignatureCanvas from 'react-signature-canvas';

const INITIALFORMVALUES: ResultVerification = {
    officialName: '',
    email: '',
    verificationObject: '',
};

const schema = yup.object({
    officialName: yup.string().required('Name Required'),
    email: yup.string().email('Input must be a valid email').required('Email Required'),
});

interface Props {
    handleBack: () => void;
    onSubmit: (cb: () => void, verification?: ResultVerification) => void;
    submissionError: string | undefined;
    matchOfficialVerification?: ResultVerification;
    matchCompleted: boolean;
    modalSize: 'small' | 'large';
    removeBackButton?: boolean;
}
interface CanvasRefProps extends HTMLCanvasElement {
    clear: () => void;
    isEmpty: () => boolean;
    getTrimmedCanvas: () => CanvasRefProps;
    toDataURL: () => string;
    _canvas: HTMLCanvasElement;
}

interface HandleSubmit {
    setStateFunction: Dispatch<SetStateAction<boolean>>;
    verification?: ResultVerification;
}

const OfficialVerificationAndFinishMatch = ({
    handleBack,
    onSubmit,
    submissionError,
    matchOfficialVerification,
    matchCompleted,
    modalSize,
    removeBackButton,
}: Props) => {
    const [initialValues, setInitialValues] = useState<ResultVerification>(INITIALFORMVALUES);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [submittingWithVerification, setSubmittingWithVerification] = useState<boolean>(false);
    const canvasRef = useRef<CanvasRefProps>(null);

    const clear = () => {
        if (canvasRef && canvasRef.current) {
            canvasRef.current.clear();
        }
    };

    const handleSubmit = ({ setStateFunction, verification }: HandleSubmit) => {
        setStateFunction(true);
        onSubmit(() => setStateFunction(false), verification);
    };

    const handleSubmitWithoutVerification = () => {
        handleSubmit({
            setStateFunction: setSubmitting,
        });
    };

    useEffect(() => {
        if (matchOfficialVerification) {
            setInitialValues((currentState) => ({
                ...currentState,
                officialName: matchOfficialVerification.officialName,
                email: matchOfficialVerification.email,
            }));
        }
    }, [matchOfficialVerification]);

    return (
        <>
            <Row className="justify-content-center mb-2">
                <Col xs="auto">
                    <strong>
                        {matchOfficialVerification ? 'Update' : 'Add'} Official Verification
                    </strong>
                </Col>
            </Row>
            <Formik
                validationSchema={schema}
                initialValues={initialValues}
                enableReinitialize={true}
                onSubmit={({ officialName, email, verificationObject }, actions) => {
                    if (canvasRef && canvasRef.current) {
                        if (canvasRef.current.isEmpty()) {
                            actions.setFieldError(
                                'verificationObject',
                                'No Signature was provided'
                            );
                            actions.setSubmitting(false);
                            return;
                        } else {
                            verificationObject = canvasRef.current.getTrimmedCanvas().toDataURL();
                        }
                    }
                    if (officialName && email && verificationObject) {
                        const verification: ResultVerification = {
                            officialName,
                            email,
                            verificationObject,
                        };
                        handleSubmit({
                            setStateFunction: setSubmittingWithVerification,
                            verification,
                        });
                    } else {
                        // should not get here as schema validations that officialName & email have ben provided & we added catches for the canvas object
                        console.error(
                            'an error occurred saving official verification. Not all inputs were provided'
                        );
                        console.error({ officialName, email, verificationObject });
                    }
                }}
            >
                {({ handleSubmit, handleChange, values, touched, errors }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <Row className="my-2">
                            <Col className="align-items-center" xs={12} lg={5}>
                                <Row>
                                    <Form.Group controlId="officialName" as={Col}>
                                        <Form.Label>
                                            <strong>Official Name</strong>
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Official Name"
                                            value={values.officialName}
                                            onChange={handleChange}
                                            isValid={touched.officialName && !errors.officialName}
                                            isInvalid={
                                                touched.officialName && !!errors.officialName
                                            }
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.officialName}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Row>
                                <Row>
                                    <Form.Group controlId="email" as={Col}>
                                        <Form.Label>
                                            <strong>Official Email</strong>
                                        </Form.Label>
                                        <Form.Control
                                            type="email"
                                            placeholder="official@cbwaterpolo.com"
                                            value={values.email}
                                            onChange={handleChange}
                                            isValid={touched.email && !errors.email}
                                            isInvalid={touched.email && !!errors.email}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.email}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Row>
                            </Col>
                            <Col xs={12} lg={modalSize === 'small' ? 12 : 7}>
                                <Row>
                                    <Col>
                                        <SignatureCanvas
                                            ref={canvasRef}
                                            backgroundColor="rgba(0,0,0,0.1)"
                                            canvasProps={{ width: 350, height: 150 }}
                                        />
                                    </Col>
                                </Row>
                                {/* if selectedStat error mock a form field error */}
                                {errors.verificationObject && touched.verificationObject && (
                                    <Row>
                                        <Col xs={12}>
                                            <div
                                                className="invalid-feedback"
                                                style={{ display: 'block' }}
                                            >
                                                {errors.verificationObject}
                                            </div>
                                        </Col>
                                    </Row>
                                )}
                                <Row>
                                    <Col>
                                        <Button size="sm" onClick={clear}>
                                            Clear Signature
                                        </Button>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                        {/* if completeMatchError mock a form field error */}
                        {submissionError && (
                            <Row>
                                <Col xs={12}>
                                    <div className="invalid-feedback" style={{ display: 'block' }}>
                                        {submissionError}
                                    </div>
                                </Col>
                            </Row>
                        )}
                        <Row className="align-items-center justify-content-between my-2">
                            {!removeBackButton && (
                                <Col xs="auto" className="my-2">
                                    <Button
                                        disabled={submitting || submittingWithVerification}
                                        onClick={handleBack}
                                        variant="outline-primary"
                                        style={{ boxShadow: 'none' }}
                                    >
                                        Back
                                    </Button>
                                </Col>
                            )}
                            {!matchCompleted && (
                                <Col xs="auto" className="my-2">
                                    <Button
                                        disabled={submittingWithVerification}
                                        onClick={handleSubmitWithoutVerification}
                                        variant="outline-success"
                                        style={{ boxShadow: 'none' }}
                                    >
                                        {submitting ? (
                                            <Spinner
                                                as="span"
                                                animation="border"
                                                size="sm"
                                                role="status"
                                                aria-hidden="true"
                                            />
                                        ) : (
                                            'Complete Match Without Official Verification'
                                        )}
                                    </Button>
                                </Col>
                            )}
                            <Col xs="auto" className="my-2">
                                <Button
                                    type="submit"
                                    disabled={submitting}
                                    variant="success"
                                    style={{ boxShadow: 'none' }}
                                >
                                    {submittingWithVerification ? (
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                    ) : matchCompleted ? (
                                        'Add Verification'
                                    ) : (
                                        'Complete Match'
                                    )}
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                )}
            </Formik>
        </>
    );
};

export default OfficialVerificationAndFinishMatch;
